import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo
} from "react";
import { generateSignedUrl } from "../../../utils/getPreSignedURL";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Typography
} from "@mui/material";
import AudioPlayer from "../AudioPlayer";
import useSignedUrl from "../../../utils/useSignedUrl";
import useSanitizeContent from "../../../utils/useSanitizeContent";
import "./RenderHtml.css";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  completeMasterclassTrainingDay,
  completeTrainings,
  getCurrentUserLevels,
  getCurrentUserMasterclassTrainings,
  updateMasterclassTrainingById,
  updatePercentage,
  updateSelectedLevel,
  updateTrainingById,
  updateTrainingProgressById
} from "../../../features/training/trainingSlice";
import {
  selectAllMasterclassTrainings,
  selectLevelData,
  selectLoading
} from "../../../features/training/trainingSelectors";
import useThemeColor from "../../../utils/useTheme";
import {
  addNotificationUser,
  fetchCurrentUser,
  getNotificationUser,
  updateUserUncompletedCount
} from "../../../features/auth/authSlice";
import { Bookmark, BookmarkBorder, Help } from "@mui/icons-material";
import FAQModal from "../../Modals/FAQModal";
import ReactConfetti from "react-confetti";
import {
  updateMasterclassTrainingProgress,
  updateTrainingProgress
} from "../../../api/trainingApi";
function convertLinks(htmlString) {
  // Create a temporary container
  const container = document.createElement("div");
  container.innerHTML = htmlString;

  // Function to process text nodes
  function processTextNodes(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      // URL regex pattern
      const urlRegex = /(https?:\/\/[^\s<>"']+)/gi;

      // If the text contains a URL
      if (urlRegex.test(node.textContent)) {
        // Create a temporary span to hold the new content
        const span = document.createElement("span");

        // Replace URLs with anchor tags
        span.innerHTML = node.textContent.replace(urlRegex, (url) => {
          return `<a href="${url}">${url}</a>`;
        });

        // Replace the text node with the new content
        const parent = node.parentNode;
        while (span.firstChild) {
          parent.insertBefore(span.firstChild, node);
        }
        parent.removeChild(node);
      }
    } else if (node.nodeType === Node.ELEMENT_NODE && node.nodeName !== "A") {
      // Process child nodes if this is not an anchor element
      Array.from(node.childNodes).forEach(processTextNodes);
    }
  }

  // Process all nodes in the container
  Array.from(container.childNodes).forEach(processTextNodes);

  return container.innerHTML;
}
const AudioItem = ({ audio, setIsAudioFinished }) => {
  const src = useSignedUrl(
    audio,
    process.env.REACT_APP_BACKEND_TRAINING_IMAGE_LOCATION
  );
  const styles = useThemeColor();
  return (
    <Box
      sx={{
        background: styles.inputLabelColor,
        width: { sm: "100%" }
      }}
      // p={"8px 20px"}
      borderRadius={"100px"}
    >
      <AudioPlayer
        setIsAudioFinished={setIsAudioFinished}
        styles={styles}
        isSliderNeeded
        src={src}
      />
    </Box>
  );
};
const isValidUrl = (url) => {
  return /^(https?:\/\/|www\.)/.test(url);
};
const RenderHtml = ({
  activeData,
  styles,
  activeDay,
  setIsAudioFinished,
  isAudioFinished,
  setIsTrainingUpdated,
  isMobile,
  selectedLevel,
  currentLevel,
  nextActiveData,
  setActiveDay,
  enabledDays,
  activeLevelForMobile,
  allLevels,
  setShowCompletionPopup,
  setCompletionPopupData
}) => {
  const [value, setValue] = useState("");
  const availableLevels = useSelector(selectLevelData);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openFAQModal, setOpenFAQModal] = useState(false);
  const [loadingHtml, setLoadingHtml] = useState(false);
  const loading = useSelector(selectLoading);

  // Track the last processed HTML to prevent unnecessary re-processing
  const lastProcessedHtmlRef = useRef("");
  const isProcessingRef = useRef(false);
  const hasInitializedRef = useRef(false);

  const updateImageSources = useCallback(
    async (html, color) => {
      // Prevent re-processing the same content
      if (html === lastProcessedHtmlRef.current && hasInitializedRef.current) {
        return;
      }

      // Prevent concurrent processing
      if (isProcessingRef.current) {
        return;
      }

      isProcessingRef.current = true;
      lastProcessedHtmlRef.current = html;
      setLoadingHtml(true);

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const images = Array.from(doc.querySelectorAll("img"));

      // Process all images in parallel for better performance
      const imagePromises = images.map(async (img) => {
        const fileName = img.getAttribute("src");

        if (
          fileName &&
          !fileName.startsWith("http") &&
          !fileName.startsWith("data:")
        ) {
          try {
            const signedUrl = await generateSignedUrl(
              fileName,
              process.env.REACT_APP_BACKEND_TRAINING_IMAGE_LOCATION
            );
            img.src = signedUrl;
          } catch (error) {
            console.error("Error generating signed URL:", error);
          }
        }

        // Performance optimizations
        if (!img.hasAttribute("loading")) {
          img.setAttribute("loading", "lazy");
        }
        if (!img.hasAttribute("decoding")) {
          img.setAttribute("decoding", "async");
        }
        if (!img.hasAttribute("referrerpolicy")) {
          img.setAttribute("referrerpolicy", "no-referrer");
        }

        // Preserve alignment from style, containerstyle, or data-text-align
        const imgStyle = img.getAttribute("style");
        const containerStyle = img.getAttribute("containerstyle");
        const dataAlign = img.getAttribute("data-text-align");

        img.style.display = "block";

        // Check containerstyle for margin-based alignment (from ImageResize extension)
        if (containerStyle) {
          // Parse margin values to determine alignment
          if (
            containerStyle.includes("margin: 0px auto 0px 0px") ||
            containerStyle.includes("margin:0px auto 0px 0px")
          ) {
            // Left aligned
            img.style.marginLeft = "0";
            img.style.marginRight = "auto";
          } else if (
            containerStyle.includes("margin: 0px 0px 0px auto") ||
            containerStyle.includes("margin:0px 0px 0px auto")
          ) {
            // Right aligned
            img.style.marginLeft = "auto";
            img.style.marginRight = "0";
          } else if (
            containerStyle.includes("margin: 0px auto") ||
            containerStyle.includes("margin:0px auto")
          ) {
            // Center aligned
            img.style.marginLeft = "auto";
            img.style.marginRight = "auto";
          }
          // Remove the custom attributes as we've converted them to standard styles
          img.removeAttribute("containerstyle");
          img.removeAttribute("wrapperstyle");
        } else if (imgStyle && imgStyle.includes("text-align")) {
          // Keep existing text-align in style
          const textAlign = imgStyle.match(
            /text-align:\s*(left|center|right)/
          )?.[1];
          if (textAlign === "left") {
            img.style.marginLeft = "0";
            img.style.marginRight = "auto";
          } else if (textAlign === "center") {
            img.style.marginLeft = "auto";
            img.style.marginRight = "auto";
          } else if (textAlign === "right") {
            img.style.marginLeft = "auto";
            img.style.marginRight = "0";
          }
        } else if (dataAlign) {
          // Convert data-text-align to margin styles
          if (dataAlign === "left") {
            img.style.marginLeft = "0";
            img.style.marginRight = "auto";
          } else if (dataAlign === "center") {
            img.style.marginLeft = "auto";
            img.style.marginRight = "auto";
          } else if (dataAlign === "right") {
            img.style.marginLeft = "auto";
            img.style.marginRight = "0";
          }
        } else {
          // Default center alignment
          img.style.marginLeft = "auto";
          img.style.marginRight = "auto";
        }

        if (!img.hasAttribute("width")) {
          img.setAttribute("width", "100%");
        }
        if (!img.hasAttribute("height")) {
          img.setAttribute("height", "100%");
        }
      });

      console.log("Processing images:", images.length);
      await Promise.all(imagePromises);

      const links = Array.from(doc.querySelectorAll("a"));
      links.forEach((link) => {
        link.target = "_blank";
        let href = link.getAttribute("href");
        link.style.background = "";
        link.style.color = "";
        if (href) {
          if (href.startsWith("www.")) {
            link.setAttribute("href", `https://${href}`);
          } else if (!isValidUrl(href)) {
            link.setAttribute("href", `https://${href}`);
          }
        }
      });

      const paragraphs = [
        ...Array.from(doc.querySelectorAll("span")),
        ...Array.from(doc.querySelectorAll("strong")),
        ...Array.from(
          doc.querySelectorAll(["h1", "h2", "h3", "h4", "h5", "h6"])
        )
      ];
      paragraphs.forEach((p) => {
        p.style.background = "";
        p.style.color = color;
      });

      const allParas = Array.from(doc.querySelectorAll("p"));
      let consecutiveJustifyBrs = [];

      allParas.forEach((p, index) => {
        if (
          p.classList.contains("ql-align-justify") &&
          p.innerHTML.trim() === "<br>"
        ) {
          consecutiveJustifyBrs.push(p);
        } else {
          if (consecutiveJustifyBrs.length > 7) {
            const lastFourElements = consecutiveJustifyBrs.slice(-4);
            lastFourElements.forEach((element) => element.remove());
          }
          consecutiveJustifyBrs = [];
        }
        p.style.color = color;
      });

      if (consecutiveJustifyBrs.length > 7) {
        const lastFourElements = consecutiveJustifyBrs.slice(-4);
        lastFourElements.forEach((element) => element.remove());
      }

      console.log("Final HTML after processing images and links.");
      setValue(convertLinks(doc.body.innerHTML));
      setLoadingHtml(false);
      hasInitializedRef.current = true;
      isProcessingRef.current = false;
    },
    [setValue]
  );
  useEffect(() => {
    let isMounted = true;

    // Reset tracking when htmlValue changes to allow reprocessing
    const currentHtml = activeData?.generalText || "<p></p>";
    if (currentHtml !== lastProcessedHtmlRef.current) {
      hasInitializedRef.current = false;
    }

    const initialHtml = activeData?.generalText || "<p></p>";

    if (isMounted) {
      console.log("Mounting or updating content in RenderHtml");
      updateImageSources(initialHtml, styles.color);
    }

    return () => {
      isMounted = false;
    };
  }, [updateImageSources, activeDay, activeData?.generalText]);

  const sanitizedContent = useSanitizeContent(value);

  // Memoize the HTML object to prevent React from recreating DOM nodes
  const htmlContent = useMemo(
    () => ({ __html: sanitizedContent }),
    [sanitizedContent]
  );
  const dispatch = useDispatch();
  const scrollToTop = useCallback(() => {
    // Wait for the next tick to ensure DOM updates have occurred
    setTimeout(() => {
      const scrollableElement = document.getElementById("current-day");
      if (scrollableElement) {
        scrollableElement.scrollIntoView({
          behavior: "instant",
          block: "start"
        });
      }
    }, 100);
  }, []);
  const masterclassData = useSelector(selectAllMasterclassTrainings);

  const [showConfetti, setShowConfetti] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onComplete = async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      if (selectedLevel?.includes("level")) {
        const completeResult = await dispatch(
          completeTrainings({
            date: dayjs().format("YYYY-MM-DD"),
            levelId: activeData?.levelId,
            trainingDayId: activeData?.trainingId
          })
        );
        if (completeResult?.payload?.error) {
          setSnackbarMessage(completeResult?.payload?.error);
          setOpenSnackbar(true);
          return;
        }
        if (completeResult?.payload?.missedDays?.length) {
          setSnackbarMessage(
            `Please complete all prior trainings before this day. Missed Days: ${completeResult?.payload?.missedDays.join(
              ", "
            )}.`
          );
          setOpenSnackbar(true);
          return;
        }
        if (completeResult.meta.requestStatus === "rejected") {
          throw new Error("completeTrainings failed");
        }

        const levelData = availableLevels?.find(
          (item) => item.levelId === activeData?.levelId
        );

        const updatedPercent =
          ((levelData?.completedTrainingDays + 1) /
            levelData?.totalTrainingDays) *
          100;

        await dispatch(
          updatePercentage({
            id: activeData?.levelId,
            updates: {
              trainingCompletionPercentage: updatedPercent,
              completedTrainingDays: levelData?.completedTrainingDays + 1
            }
          })
        );
        await dispatch(updateUserUncompletedCount());
        if (updatedPercent === 100) {
          const currentDate = dayjs().format("YYYY-MM-DD");
          dispatch(addNotificationUser(currentDate));
          dispatch(fetchCurrentUser(currentDate));
          dispatch(getCurrentUserLevels(currentDate));
        }
        // Only run this if the first dispatch succeeded
        setTimeout(() => {
          dispatch(
            updateTrainingById({
              id: activeData?.trainingId,
              updates: { completedOn: dayjs().format("YYYY-MM-DD HH:mm:ss") }
            })
          );
        }, 5000);

        // Handle day navigation after confetti
        // Check if next day exists - verify both data and total days
          const nextDay = activeDay + 1;
        const totalDays =
          currentLevel?.totalTrainingDays || enabledDays?.length || 30;
        const hasNextDay = nextActiveData && nextDay <= totalDays;

        if (hasNextDay) {
          // Update localStorage IMMEDIATELY to prevent race condition with calculateActiveDay
          localStorage.setItem(
            "lastProgramVisitedLevel",
            JSON.stringify({
              level: selectedLevel,
              activeDay: nextDay
            })
          );
          dispatch(updateSelectedLevel(selectedLevel));

          // Change day after confetti duration (visual update delayed for UX)
          setTimeout(() => {
            setActiveDay(nextDay);
            setIsTrainingUpdated(true);
          }, 4000); // 4 seconds to allow confetti to complete
        } else {
          // This is the last day of the training
          localStorage.setItem(
            "lastProgramVisitedLevel",
            JSON.stringify({
              level: selectedLevel,
              activeDay
            })
          );
          console.log("🚀 ~ onComplete ~ selectedLevel:", selectedLevel);
          dispatch(updateSelectedLevel(selectedLevel));

          // Show completion popup on mobile when last day is completed
          if (setShowCompletionPopup && setCompletionPopupData && !nextActiveData) {
            // Find the next unlocked level - FIX: use levelName instead of level
            const currentLevelIndex = allLevels?.findIndex(
              (level) => level.levelName === selectedLevel
            );
            const nextLevel =
              currentLevelIndex !== -1 &&
              currentLevelIndex < allLevels.length - 1
                ? allLevels[currentLevelIndex + 1]
                : null;

            setCompletionPopupData({
              trainingName: currentLevel?.title || "training",
              nextLevel: nextLevel
            });

            // Show popup after confetti completes
            setTimeout(() => {
              setShowCompletionPopup(true);
            }, 4000);
          }
        }
      } else {
        const completeResult = await dispatch(
          completeMasterclassTrainingDay({
            date: dayjs().format("YYYY-MM-DD"),
            levelId: activeData?.levelId,
            trainingDayId: activeData?.trainingId
          })
        );
        if (completeResult?.payload?.missedDays?.length) {
          setSnackbarMessage(
            `Please complete all prior trainings before this day. Missed Days: ${completeResult?.payload?.missedDays.join(
              ", "
            )}.`
          );
          setOpenSnackbar(true);
          return;
        }
        // Check if the first dispatch was rejected
        if (completeResult.meta.requestStatus === "rejected") {
          throw new Error("completeTrainings failed");
        }

        // Handle day navigation after confetti
        // For masterclass, check if next day exists and is unlocked
        if (nextActiveData && nextActiveData?.isUnlocked) {
          const nextDay = activeDay + 1;
          localStorage.setItem(
            "lastProgramVisitedLevel",
            JSON.stringify({
              level: selectedLevel,
              activeDay: nextDay
            })
          );

          // Change day after confetti duration
          setTimeout(() => {
            setActiveDay(nextDay);
          }, 4000); // 4 seconds to allow confetti to complete
        } else {
          // This is the last day of the masterclass training
          localStorage.setItem(
            "lastProgramVisitedLevel",
            JSON.stringify({
              level: selectedLevel,
              activeDay
            })
          );
          dispatch(updateSelectedLevel(selectedLevel));

          // Show completion popup on mobile when last day is completed
          if (setShowCompletionPopup && setCompletionPopupData && !nextActiveData) {
            // Get proper training name for masterclass sections
            let trainingName = "training";
            if (selectedLevel === "Overview") {
              trainingName = `${masterclassData?.title || "Masterclass"} Overview`;
            } else if (selectedLevel === "10Principles") {
              trainingName = "10 Principles of Nutrition";
            } else if (selectedLevel === "CoachesVault") {
              trainingName = "Coaches Vault";
            }

            // Find the next unlocked masterclass section
            const masterclassSections = Object.keys(
              masterclassData.trainings || {}
            );
            const currentSectionIndex = masterclassSections.findIndex(
              (section) => section === selectedLevel
            );

            // Get next masterclass section if available
            // let nextLevel = null;
            // if (
            //   currentSectionIndex !== -1 &&
            //   currentSectionIndex < masterclassSections.length - 1
            // ) {
            //   const nextSection = masterclassSections[currentSectionIndex + 1];
            //   // Check if next section has unlocked trainings
            //   const nextSectionTrainings =
            //     masterclassData.trainings[nextSection] || [];
            //   const hasUnlockedTrainings = nextSectionTrainings.some(
            //     (training) => training?.isUnlocked
            //   );

            //   if (hasUnlockedTrainings) {
            //     nextLevel = {
            //       levelName: nextSection,
            //       levelActive: true,
            //       title:
            //         nextSection === "10Principles"
            //           ? "10 Principles of Nutrition"
            //           : nextSection === "CoachesVault"
            //             ? "Coaches Vault"
            //             : nextSection
            //     };
            //   }
            // }

            setCompletionPopupData({
              trainingName: trainingName,
              nextLevel: null, // No next level for masterclass, or you can pass nextLevel if you want to show it in the popup
            });

            // Show popup after confetti completes
            setTimeout(() => {
              setShowCompletionPopup(true);
            }, 4000);
          }
        }
        dispatch(
          getCurrentUserMasterclassTrainings(dayjs().format("YYYY-MM-DD"))
        );
      }
      // Show confetti after modal closes
      setShowConfetti(true);
      setIsAudioFinished(false);
      scrollToTop();
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isAudioFinished && !activeData?.completedOn) onComplete();
  }, [isAudioFinished, activeData]);

  const handleFavorite = async () => {
    // Store current activeDay before operations to ensure it doesn't change
    const currentActiveDay = activeDay;

    if (selectedLevel?.includes("level")) {
      dispatch(
        updateTrainingById({
          id: activeData?.trainingId,
          updates: { isFavorite: !activeData?.isFavorite }
        })
      );

      dispatch(
        updateTrainingProgressById({
          id: activeData?.trainingId,
          updates: { isFavorite: !activeData?.isFavorite }
        })
      );

      await updateTrainingProgress({
        trainingId: activeData?.trainingId,
        isFavorite: !activeData?.isFavorite
      });
    } else {
      dispatch(
        updateMasterclassTrainingById({
          id: activeData.trainingId,
          updates: {
            isFavorite: !activeData.isFavorite
          }
        })
      );
      await updateMasterclassTrainingProgress({
        trainingId: activeData.trainingId,
        isFavorite: !activeData.isFavorite
      });
    }
    localStorage.setItem(
      "lastProgramVisitedLevel",
      JSON.stringify({
        level: selectedLevel,
        activeDay: currentActiveDay
      })
    );
  };
  return (
    <Box
      id="current-day"
      display={"flex"}
      flexDirection="column"
      mt={selectedLevel === "Overview" ? "20px" : "0px"}
      gap="10px"
      sx={{ userSelect: "none" }}
    >
      <Box display={"flex"} justifyContent={"space-between"}>
        <Typography fontWeight={600} fontSize="28px">
          {activeData?.title}
        </Typography>

        {!isMobile && selectedLevel !== "Overview" && (
          <IconButton onClick={handleFavorite}>
            {activeData?.isFavorite ? (
              <Bookmark
                sx={{
                  height: "28px",
                  width: "28px",
                  fill: "#03B55C"
                }}
              />
            ) : (
              <BookmarkBorder
                sx={{
                  height: "28px",
                  width: "28px",
                  fill: styles.color
                }}
              />
            )}
          </IconButton>
        )}
      </Box>

      <Typography fontSize={"18px"}>{activeData?.subTitle}</Typography>
      {/* {isMobile && selectedLevel !== "Overview" && (
        <Button
          onClick={handleFavorite}
          variant={!activeData?.isFavorite ? "outlined" : "contained"}
          sx={{
            backgroundColor: activeData?.isFavorite ? "#03B55C" : "transparent",
            color: activeData?.isFavorite ? "#fff" : "#03B55C",
            minWidth: "auto",
            margin: "0 auto",
            textTransform: "none",
            borderColor: "#03B55C"
          }}
        >
          Favorite
          {activeData?.isFavorite ? (
            <Bookmark
              sx={{
                height: "16px",
                width: "16px",
                marginLeft: "5px",
                fill: "#fff"
              }}
            />
          ) : (
            <BookmarkBorder
              sx={{
                height: "16px",
                width: "16px",
                marginLeft: "5px",
                fill: "#03B55C"
              }}
            />
          )}
        </Button>
      )} */}
      {/* Fixed container for the audio player */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          // backgroundColor: styles.backgroundColor || "#fff",
          paddingBottom: "10px",
          paddingTop: "10px",
          width: "100%"
        }}
      >
        <AudioItem
          setIsAudioFinished={setIsAudioFinished}
          audio={activeData?.audioFile}
        />
      </Box>

      {/* Content section with appropriate padding */}
      <Box
        sx={{
          marginTop: "10px", // Add spacing after the fixed AudioItem
          width: "100%",
          overflow: "auto"
        }}
      >
        {loadingHtml || loading ? (
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            my={10}
          >
            <CircularProgress
              sx={{ width: "100%", justifyContent: "center" }}
            />
          </Box>
        ) : sanitizedContent && sanitizedContent !== "undefined" ? (
          <Box
            sx={{
              width: "100%",
              // backgroundColor: "background.paper",
              borderRadius: { xs: 1, sm: 2 },
              p: { xs: 1, sm: 2 },
              boxShadow: 1,
              opacity: loadingHtml ? 0.6 : 1,
              transition: "opacity 0.2s ease-in-out"
            }}
          >
            <div
              className="preview-html tiptap-editor"
              style={{
                marginTop: "10px",
                background: styles.background,
                ".ql-align-center": {
                  display: "flex",
                  justifyContent: "center"
                },
                p: {
                  color: `${styles.color} !important`
                },
                h1: {
                  color: `${styles.color} !important`
                }
              }}
              dangerouslySetInnerHTML={htmlContent}
            />
          </Box>
        ) : loadingHtml ? (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Loading content...
            </Typography>
          </Box>
        ) : (
          <></>
        )}
      </Box>

      <Snackbar
        open={openSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {showConfetti && (
        <ReactConfetti
          width={window.innerWidth}
          height={window.innerHeight}
          numberOfPieces={isMobile ? 600 : 1200}
          recycle={false}
          colors={["#368AD0", "#2a6ba4", "#1d4d7a", "#62a6e0", "#90c1e8"]}
          onConfettiComplete={() => setShowConfetti(false)}
        />
      )}
      {activeData && selectedLevel !== "Overview" ? (
        <Button
          variant="contained"
          color="primary"
          onClick={onComplete}
          disabled={activeData?.completedOn}
          sx={{
            marginTop: 3.1,
            backgroundColor: "#368AD0",
            cursor: "pointer",
            color: "#fff",
            width: "100%",
            height: "42px",
            fontWeight: "bold",
            textTransform: "none",
            ":disabled": {
              backgroundColor: "#A7C8E9",
              color: "#64AEDD",
              pointerEvents: "none"
            }
          }}
        >
          Complete{activeData?.completedOn ? "d" : ""}
        </Button>
      ) : (
        <></>
      )}
      {(isMobile ? selectedLevel && activeLevelForMobile : selectedLevel) &&
        selectedLevel?.includes("level") && (
          <Box
            sx={{
              position: "fixed",
              display: "flex",
              gap: "20px",
              width: "50%",
              bottom: { xs: 95, sm: 20 },
              zIndex: { xs: 100000, md: "unset" }
            }}
          >
            <IconButton
              variant="contained"
              sx={{
                display: "flex",
                backgroundColor: "#FF4000",
                color: "#FCFCFC",
                "&:hover": {
                  backgroundColor: "#FF7043"
                }
              }}
              onClick={() => setOpenFAQModal((prev) => !prev)}
            >
              <Help />
            </IconButton>
            <FAQModal
              htmlValue={currentLevel?.faq}
              open={openFAQModal}
              onClose={() => setOpenFAQModal(false)}
              programTitle={currentLevel?.title}
            />
          </Box>
        )}
    </Box>
  );
};

export default RenderHtml;
